/**
 * SimulatedTransport — fully working cross-tab mesh for local development.
 *
 * Uses BroadcastChannel so every tab of BisaWofa on the same origin acts as
 * a peer. Open two browser windows and they discover each other, exchange
 * announces and relay packets — the same code path that a real BLE
 * transport will follow later.
 *
 * This transport is the workhorse for:
 *   - UI iteration on the Nearby panel
 *   - Protocol-layer debugging without device hardware
 *   - Automated tests (can be driven headlessly)
 *
 * It does NOT provide any actual BLE connectivity. WebBluetoothTransport
 * (scan-only) and the future CapacitorTransport will handle real radios.
 */

import { BleTransport } from './BleTransport.js';

const CHANNEL_NAME      = 'bisawofa-mesh-sim';
const ANNOUNCE_INTERVAL = 3000;  // ms — re-announce presence
const PEER_TIMEOUT_MS   = 10000; // ms — declare peer lost after this silence

export class SimulatedTransport extends BleTransport {
  constructor(opts) {
    super(opts);
    this.channel = null;
    this.announceTimer = null;
    this.reapTimer = null;
    /** @type {Map<string, { peer: any, lastSeen: number }>} */
    this.seenPeers = new Map();
  }

  get capabilities() {
    return { canScan: true, canAdvertise: true, canRelay: true, name: 'Simulated (BroadcastChannel)' };
  }

  async start() {
    if (this.started) return;
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.addEventListener('message', (ev) => this._onMessage(ev.data));

    // Immediately advertise ourselves, then on interval
    this._announce();
    this.announceTimer = setInterval(() => this._announce(), ANNOUNCE_INTERVAL);

    // Periodically expire peers we haven't heard from
    this.reapTimer = setInterval(() => this._reapPeers(), 2000);

    this.started = true;
    console.log('[SimulatedTransport] Started on BroadcastChannel:', CHANNEL_NAME);
  }

  async stop() {
    if (!this.started) return;
    clearInterval(this.announceTimer);
    clearInterval(this.reapTimer);
    // Send a polite leave so peers drop us faster than the timeout
    try { this.channel?.postMessage({ kind: 'leave', peerId: this._hex(this.identity.peerId) }); } catch (_) {}
    this.channel?.close();
    this.channel = null;
    this.seenPeers.clear();
    this.started = false;
    console.log('[SimulatedTransport] Stopped');
  }

  async send(packetBytes, recipientId = null) {
    if (!this.started) return;
    // Copy into a plain array so BroadcastChannel structured-clones cleanly
    const payload = { kind: 'packet', bytes: Array.from(packetBytes) };
    if (recipientId) payload.to = this._hex(recipientId);
    this.channel.postMessage(payload);
  }

  // ---------- internal ----------

  _announce() {
    if (!this.channel) return;
    const msg = {
      kind: 'announce',
      peerId:   this._hex(this.identity.peerId),
      nickname: this.identity.nickname,
      ts:       Date.now(),
    };
    this.channel.postMessage(msg);
    console.log('[SimulatedTransport] announce ->', msg.nickname, msg.peerId.slice(0, 8));
  }

  _onMessage(data) {
    if (!data || typeof data !== 'object') return;
    // Ignore our own echoes (BroadcastChannel doesn't loop back in most
    // browsers, but be defensive)
    const localHex = this._hex(this.identity.peerId);

    console.log('[SimulatedTransport] rx', data.kind, data.peerId?.slice(0, 8));

    if (data.kind === 'announce') {
      if (data.peerId === localHex) return;
      const peer = {
        id:        this._unhex(data.peerId),
        idHex:     data.peerId,
        nickname:  data.nickname || 'unknown',
        lastSeen:  data.ts || Date.now(),
        rssi:      0,
        transport: 'simulated',
      };
      this.seenPeers.set(data.peerId, { peer, lastSeen: peer.lastSeen });
      this.handlers.onPeerSeen(peer);
      return;
    }

    if (data.kind === 'leave') {
      if (data.peerId === localHex) return;
      const entry = this.seenPeers.get(data.peerId);
      if (entry) {
        this.seenPeers.delete(data.peerId);
        this.handlers.onPeerLost(entry.peer.id);
      }
      return;
    }

    if (data.kind === 'packet') {
      // Drop packets addressed to a specific peer that isn't us
      if (data.to && data.to !== localHex) return;
      const bytes = new Uint8Array(data.bytes);
      this.handlers.onPacket(bytes, null);
      return;
    }
  }

  _reapPeers() {
    const now = Date.now();
    for (const [hex, entry] of this.seenPeers.entries()) {
      if (now - entry.lastSeen > PEER_TIMEOUT_MS) {
        this.seenPeers.delete(hex);
        this.handlers.onPeerLost(entry.peer.id);
      }
    }
  }

  _hex(u8) {
    return Array.from(u8, b => b.toString(16).padStart(2, '0')).join('');
  }
  _unhex(hex) {
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
    return out;
  }
}

export default SimulatedTransport;
